// ========================================
// TENANT-AWARE API SERVICE
// ========================================

import config from '../config';
import { DURATION_MAP } from '../constants/dashboard';
import {
    addUserToGroupAction,
    removeUserFromGroupAction,
    blockTeamAction,
    unblockTeamAction,
    disableUserAccount as disableUserAccountAction,
    enableUserAccount as enableUserAccountAction,
    getDisabledUserStatus as getDisabledUserStatusAction,
    getUserById as getUserByIdAction,
    setTenantConfig as setApiHelpersTenantConfig,
} from './apiHelpers';
import { authenticatedFetch } from './authService';

let currentTenantConfig = null;

// Health check cache to prevent excessive API calls
let healthCheckCache = null;
let healthCheckCacheTime = 0;
const HEALTH_CHECK_CACHE_TTL = 3600000; // Cache for 60 minutes

/**
 * Set the current tenant configuration
 * This updates all API endpoints to use tenant-specific URLs and credentials
 *
 * @param {Object} tenantConfig - Tenant configuration with litellmUrl, openwebuiUrl, etc.
 */
export const setTenantConfig = (tenantConfig) => {
    currentTenantConfig = tenantConfig;
    setApiHelpersTenantConfig(tenantConfig);
    // Clear health check cache when tenant changes
    healthCheckCache = null;
    healthCheckCacheTime = 0;
};

/**
 * Get current API endpoints based on active tenant
 * Falls back to default config if no tenant is selected
 *
 * IMPORTANT: All requests now go through the backend proxy to support environment switching.
 * The proxy automatically routes to prod/dev based on the backend's current environment setting.
 *
 * SECURITY: API keys are no longer stored on the frontend.
 * All requests go through the backend proxy which adds authentication.
 *
 * @returns {Object} Endpoint configuration with litellm, openwebui URLs
 */
const getCurrentEndpoints = () => {
    // Always use backend proxy for environment-aware routing
    // The proxy routes to prod/dev based on backend's current environment
    return {
        litellm: `${config.backendApiUrl}/proxy/litellm`,
        openwebui: `${config.backendApiUrl}/proxy/openwebui`,
        tenantId: currentTenantConfig?.id || 'default'
    };
};

/**
 * Make an authenticated GET request to LiteLLM API via backend proxy
 * The backend proxy automatically handles environment switching
 *
 * @param {string} path - API path (e.g., '/health' or 'health')
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Response data
 */
const litellmGet = async (path, params = {}) => {
    const endpoints = getCurrentEndpoints();
    const queryString = new URLSearchParams(params).toString();
    const url = `${endpoints.litellm}${path}${queryString ? '?' + queryString : ''}`;

    const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return response.json();
};

/**
 * Make an authenticated POST request to LiteLLM API via backend proxy
 * The backend proxy automatically handles environment switching
 *
 * @param {string} path - API path (e.g., '/budget/new')
 * @param {Object} body - Request body
 * @returns {Promise<Object>} Response data
 */
const litellmPost = async (path, body = {}) => {
    const endpoints = getCurrentEndpoints();
    const url = `${endpoints.litellm}${path}`;

    const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        const errorText = await response.text();
        const error = new Error(`HTTP ${response.status}: ${errorText}`);
        // Try to parse error details for better error messages
        try {
            const parsed = JSON.parse(errorText);
            error.response = { data: parsed };
        } catch (e) {
            error.response = { data: { error: errorText } };
        }
        throw error;
    }

    return response.json();
};

/**
 * Make an authenticated DELETE request to LiteLLM API via backend proxy
 * Note: LiteLLM uses POST for delete operations with specific endpoints
 * The backend proxy automatically handles environment switching
 *
 * @param {string} path - API path (e.g., '/team/member_delete')
 * @param {Object} body - Request body
 * @returns {Promise<Object>} Response data
 */
const litellmDelete = async (path, body = {}) => {
    const endpoints = getCurrentEndpoints();
    const url = `${endpoints.litellm}${path}`;

    const response = await authenticatedFetch(url, {
        method: 'POST', // LiteLLM uses POST for delete operations
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        const errorText = await response.text();
        const error = new Error(`HTTP ${response.status}: ${errorText}`);
        // Try to parse error details for better error messages
        try {
            const parsed = JSON.parse(errorText);
            error.response = { data: parsed };
        } catch (e) {
            error.response = { data: { error: errorText } };
        }
        throw error;
    }

    return response.json();
};

// ========================================
// SYSTEM HEALTH ENDPOINTS
// ========================================

/**
 * Ping LiteLLM via the backend proxy using /health/readiness.
 * Goes through the proxy so the API key is injected server-side — no unauthenticated
 * requests hit LiteLLM directly, which previously generated "No api key" log spam.
 */
export const pingLiteLLM = async () => {
    const endpoints = getCurrentEndpoints();
    const start = Date.now();
    try {
        const response = await authenticatedFetch(`${endpoints.litellm}/health/readiness`, {
            signal: AbortSignal.timeout(10000),
        });
        const latency = Date.now() - start;
        if (!response.ok) return { reachable: false, latency, error: `HTTP ${response.status}` };
        return { reachable: true, latency, error: null };
    } catch (error) {
        return { reachable: false, latency: Date.now() - start, error: error.message };
    }
};

/**
 * Ping OpenWebUI via the backend proxy using /api/config.
 * Same approach as pingLiteLLM — avoids unauthenticated direct browser requests.
 */
export const pingOpenWebUI = async () => {
    const endpoints = getCurrentEndpoints();
    const start = Date.now();
    try {
        const response = await authenticatedFetch(`${endpoints.openwebui}/api/config`, {
            signal: AbortSignal.timeout(10000),
        });
        const latency = Date.now() - start;
        if (!response.ok) return { reachable: false, latency, error: `HTTP ${response.status}` };
        return { reachable: true, latency, error: null };
    } catch (error) {
        return { reachable: false, latency: Date.now() - start, error: error.message };
    }
};

/**
 * Get LiteLLM system health status with retry logic and caching
 * Note: LiteLLM's /health endpoint tests all model providers which can be slow.
 * We use retries to filter out transient Azure API errors and only report persistent issues.
 * Results are cached for 60 seconds to prevent excessive API calls.
 *
 * @param {number} retries - Number of retry attempts (default: 2)
 * @param {boolean} forceRefresh - Skip cache and force a fresh health check (default: false)
 * @returns {Promise<Object>} Health status data
 */
export const getSystemHealth = async (retries = 2, forceRefresh = false) => {
    // Check cache first (unless force refresh)
    if (!forceRefresh && healthCheckCache && (Date.now() - healthCheckCacheTime < HEALTH_CHECK_CACHE_TTL)) {
        console.log(`[Health Check] Returning cached result (${Math.floor((Date.now() - healthCheckCacheTime) / 1000)}s old)`);
        return healthCheckCache;
    }

    const endpoints = getCurrentEndpoints();
    const url = `${endpoints.litellm}/health`;

    let lastError = null;
    let attempts = 0;

    // Try up to (retries + 1) times
    while (attempts <= retries) {
        attempts++;

        try {
            // Create an AbortController for timeout (15 seconds per attempt)
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);

            const response = await authenticatedFetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            // Success - filter out transient errors from unhealthy_endpoints
            // Only include endpoints that have been failing consistently
            if (data.unhealthy_endpoints && Array.isArray(data.unhealthy_endpoints)) {
                // Filter out "internal error" messages as they're often transient Azure issues
                data.unhealthy_endpoints = data.unhealthy_endpoints.filter(endpoint => {
                    const error = endpoint.error || '';
                    // Keep the error if it's NOT a generic "internal error" from Azure
                    const isTransientError = error.includes('unable to complete inference due to an internal error');
                    return !isTransientError;
                });
            }

            console.log(`[Health Check] Success on attempt ${attempts}/${retries + 1}`);

            // Cache the successful result
            const result = { success: true, data };
            healthCheckCache = result;
            healthCheckCacheTime = Date.now();

            return result;

        } catch (error) {
            lastError = error;
            const isTimeout = error.name === 'AbortError';
            console.warn(`[Health Check] Attempt ${attempts}/${retries + 1} failed:`, isTimeout ? 'Timeout after 15s' : error.message);

            // If this wasn't the last attempt, wait 2 seconds before retrying
            if (attempts <= retries) {
                console.log(`[Health Check] Retrying in 2 seconds...`);
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
        }
    }

    // All retries failed - return graceful degraded response
    const isTimeout = lastError?.name === 'AbortError';
    console.error(`[Health Check] All ${retries + 1} attempts failed`);
    const result = {
        success: false,
        error: isTimeout ? 'Health check timeout' : lastError?.message,
        degraded: true,
        message: 'Health check timed out - system may still be operational'
    };

    // Cache the failed result too (to prevent hammering a failing endpoint)
    healthCheckCache = result;
    healthCheckCacheTime = Date.now();

    return result;
};

/**
 * GET /api/model-health — returns the last persisted model health check result.
 */
export const getModelHealthCache = async () => {
    try {
        const backendUrl = process.env.REACT_APP_BACKEND_API_URL || '/api';
        const response = await authenticatedFetch(`${backendUrl}/model-health`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    } catch (err) {
        return { success: false, error: err.message, cache: null };
    }
};

/**
 * POST /api/model-health/check — triggers a fresh model health check (admin only).
 */
export const triggerModelHealthCheck = async () => {
    try {
        const backendUrl = process.env.REACT_APP_BACKEND_API_URL || '/api';
        const response = await authenticatedFetch(`${backendUrl}/model-health/check`, { method: 'POST' });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || `HTTP ${response.status}`);
        }
        return await response.json();
    } catch (err) {
        return { success: false, error: err.message, cache: null };
    }
};

/**
 * Get OpenWebUI system health status
 *
 * @returns {Promise<Object>} Health status data
 */
export const getOpenWebUIHealth = async () => {
    try {
        const endpoints = getCurrentEndpoints();
        const response = await authenticatedFetch(`${endpoints.openwebui}/api/config`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) throw new Error('OpenWebUI not reachable');
        const data = await response.json();
        return { success: true, data };
    } catch (err) {
        console.error('OpenWebUI health check failed:', err);
        return { success: false, error: err.message };
    }
};

// ========================================
// MODEL MANAGEMENT ENDPOINTS
// ========================================

/**
 * Get available models from LiteLLM
 *
 * @returns {Promise<Object>} List of available models
 */
export const getLiteLLMModels = async () => {
    try {
        const data = await litellmGet('/models');
        return { success: true, data };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Get available models from OpenWebUI
 *
 * @returns {Promise<Object>} List of available models
 */
export const getOpenWebUIModels = async () => {
    try {
        const endpoints = getCurrentEndpoints();
        const response = await authenticatedFetch(`${endpoints.openwebui}/api/models`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const data = await response.json();
        return { success: true, data };
    } catch (err) {
        console.error('Error fetching OpenWebUI models:', err);
        return { success: false, error: err.message };
    }
};

// ========================================
// OPENWEBUI TOOLS ENDPOINTS
// ========================================

export const getOpenWebUISkills = async () => {
    try {
        const endpoints = getCurrentEndpoints();
        const response = await authenticatedFetch(`${endpoints.openwebui}/api/v1/skills/`, {
            headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const data = await response.json();
        const skills = Array.isArray(data) ? data.filter(s => s.is_active !== false) : [];
        return { success: true, data: skills };
    } catch (err) {
        console.error('Error fetching OpenWebUI skills:', err);
        return { success: false, error: err.message, data: [] };
    }
};

export const getOpenWebUITools = async () => {
    try {
        const endpoints = getCurrentEndpoints();
        const response = await authenticatedFetch(`${endpoints.openwebui}/api/v1/tools/list`, {
            headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const data = await response.json();
        const tools = Array.isArray(data) ? data : [];
        return { success: true, data: tools };
    } catch (err) {
        console.error('Error fetching OpenWebUI tools:', err);
        return { success: false, error: err.message, data: [] };
    }
};

// ========================================
// MCP SERVER ENDPOINTS
// ========================================

/**
 * Get MCP servers configured in OpenWebUI (tool_servers config).
 * Returns only entries with type="mcp".
 */
export const getMCPServers = async () => {
    try {
        const endpoints = getCurrentEndpoints();
        const response = await authenticatedFetch(`${endpoints.openwebui}/api/v1/configs/tool_servers`, {
            headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const data = await response.json();
        const servers = (data.TOOL_SERVER_CONNECTIONS || []).filter(s => s.type === 'mcp');
        return { success: true, data: servers, raw: data };
    } catch (err) {
        console.error('Error fetching MCP servers:', err);
        return { success: false, error: err.message, data: [] };
    }
};

/**
 * Add a new MCP server to OpenWebUI tool_servers config.
 * Reads the current config, appends the new entry, and POSTs the full array back.
 *
 * @param {Object} server - { name, url, apiKey, description }
 * @param {string} userEmail - for audit log
 */
export const addMCPServer = async (server, userEmail) => {
    try {
        const endpoints = getCurrentEndpoints();
        // Read current config first
        const getRes = await authenticatedFetch(`${endpoints.openwebui}/api/v1/configs/tool_servers`, {
            headers: { 'Content-Type': 'application/json' },
        });
        if (!getRes.ok) throw new Error(`HTTP ${getRes.status}: ${getRes.statusText}`);
        const current = await getRes.json();

        const id = server.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const newEntry = {
            url: server.url,
            path: 'openapi.json',
            type: 'mcp',
            auth_type: server.apiKey ? 'bearer' : 'none',
            headers: server.apiKey ? {
                'x-litellm-api-key': `Bearer ${server.apiKey}`,
                'Accept': 'application/json, text/event-stream'
            } : { 'Accept': 'application/json, text/event-stream' },
            key: server.apiKey || '',
            config: {
                enable: true,
                function_name_filter_list: '',
                access_grants: [{ principal_type: 'user', principal_id: '*', permission: 'read' }]
            },
            info: { id, name: server.name, description: server.description || '' },
            spec_type: 'url',
            spec: ''
        };

        const updated = [...(current.TOOL_SERVER_CONNECTIONS || []), newEntry];
        const postRes = await authenticatedFetch(`${endpoints.openwebui}/api/v1/configs/tool_servers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ TOOL_SERVER_CONNECTIONS: updated }),
        });
        if (!postRes.ok) throw new Error(`HTTP ${postRes.status}: ${postRes.statusText}`);

        if (userEmail) {
            await authenticatedFetch(`${config.backendApiUrl}/audit-log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'mcp_server_added', user_email: userEmail, details: { name: server.name, url: server.url } }),
            }).catch(() => {});
        }
        return { success: true };
    } catch (err) {
        console.error('Error adding MCP server:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Remove an MCP server from OpenWebUI tool_servers config by its info.id.
 *
 * @param {string} serverId - the info.id of the server to remove
 * @param {string} serverName - for audit log
 * @param {string} userEmail - for audit log
 */
export const deleteMCPServer = async (serverId, serverName, userEmail) => {
    try {
        const endpoints = getCurrentEndpoints();
        const getRes = await authenticatedFetch(`${endpoints.openwebui}/api/v1/configs/tool_servers`, {
            headers: { 'Content-Type': 'application/json' },
        });
        if (!getRes.ok) throw new Error(`HTTP ${getRes.status}: ${getRes.statusText}`);
        const current = await getRes.json();

        const updated = (current.TOOL_SERVER_CONNECTIONS || []).filter(s => s.info?.id !== serverId);
        const postRes = await authenticatedFetch(`${endpoints.openwebui}/api/v1/configs/tool_servers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ TOOL_SERVER_CONNECTIONS: updated }),
        });
        if (!postRes.ok) throw new Error(`HTTP ${postRes.status}: ${postRes.statusText}`);

        if (userEmail) {
            await authenticatedFetch(`${config.backendApiUrl}/audit-log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'mcp_server_removed', user_email: userEmail, details: { id: serverId, name: serverName } }),
            }).catch(() => {});
        }
        return { success: true };
    } catch (err) {
        console.error('Error deleting MCP server:', err);
        return { success: false, error: err.message };
    }
};

// ========================================
// USER MANAGEMENT ENDPOINTS
// ========================================

/**
 * Get users from OpenWebUI with pagination
 *
 * @param {number} page - Page number for pagination
 * @returns {Promise<Object>} List of users with pagination data
 */
export const getOpenWebUIUsers = async (page = 1) => {
    try {
        const endpoints = getCurrentEndpoints();
        const response = await authenticatedFetch(
            `${endpoints.openwebui}/api/v1/users/?page=${page}&order_by=created_at&direction=asc`,
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const data = await response.json();
        return { success: true, data };
    } catch (err) {
        console.error('Error fetching OpenWebUI users:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get detailed profile for a specific user
 *
 * @param {string} userId - User identifier
 * @returns {Promise<Object>} User profile data
 */
export const getOpenWebUIUserProfile = async (userId) => {
    try {
        const endpoints = getCurrentEndpoints();
        const response = await authenticatedFetch(`${endpoints.openwebui}/api/v1/users/${userId}`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const data = await response.json();
        return { success: true, data };
    } catch (err) {
        console.error(`Error fetching user profile (${userId}):`, err);
        return { success: false, error: err.message };
    }
};

/**
 * Get all groups with their member lists
 * Fetches all users across pages to build complete group membership
 *
 * @returns {Promise<Object>} List of groups with user_ids arrays
 */
export const getOpenWebUIGroups = async () => {
    try {
        const endpoints = getCurrentEndpoints();
        const groupsResponse = await authenticatedFetch(`${endpoints.openwebui}/api/v1/groups/`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!groupsResponse.ok) throw new Error(`HTTP ${groupsResponse.status}: ${groupsResponse.statusText}`);
        const groupsList = await groupsResponse.json();
        const groupsArray = Array.isArray(groupsList) ? groupsList : (groupsList.data || []);

        const usersResponse = await authenticatedFetch(
            `${endpoints.openwebui}/api/v1/users/?page=1&order_by=created_at&direction=asc`,
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );
        if (!usersResponse.ok) {
            return { success: true, data: groupsArray };
        }

        const usersData = await usersResponse.json();
        const users = usersData.users || [];
        const totalUsers = usersData.total || users.length;
        const totalPages = Math.ceil(totalUsers / 30);

        let allUsers = [...users];
        if (totalPages > 1) {
            const pagePromises = [];
            for (let page = 2; page <= totalPages; page++) {
                pagePromises.push(
                    authenticatedFetch(`${endpoints.openwebui}/api/v1/users/?page=${page}&order_by=created_at&direction=asc`, {
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    }).then(res => res.ok ? res.json() : null)
                );
            }
            const remainingPages = await Promise.all(pagePromises);
            remainingPages.forEach(pageData => {
                if (pageData && pageData.users) {
                    allUsers = allUsers.concat(pageData.users);
                }
            });
        }

        const groupsWithMembers = groupsArray.map(group => {
            const user_ids = allUsers
                .filter(user => user.group_ids && user.group_ids.includes(group.id))
                .map(user => user.id);
            return { ...group, user_ids: user_ids };
        });

        return { success: true, data: groupsWithMembers };
    } catch (err) {
        console.error('Error fetching groups:', err);
        return { success: false, error: err.message, data: [] };
    }
};

/**
 * Get count of currently active OpenWebUI users.
 * Delegates to the backend /api/active-users endpoint which caches results for
 * 2 minutes — prevents rate-limiting OpenWebUI with per-user /active calls from
 * multiple browser tabs polling every 60 seconds.
 */
export const getOpenWebUIActiveUsers = async () => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/active-users`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        if (!result.success) throw new Error(result.error || 'Unknown error');
        return { success: true, data: result.data };
    } catch (err) {
        return { success: false, error: err.message };
    }
};

// ========================================
// BUDGET MANAGEMENT ENDPOINTS
// ========================================

/**
 * Get list of all budgets from LiteLLM
 *
 * @returns {Promise<Object>} List of budgets with usage data
 */
export const getBudgetList = async () => {
    try {
        const data = await litellmGet('/budget/list');
        return { success: true, data };
    } catch (error) {
        console.error('Error fetching budget list:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Create a new budget for a user
 *
 * @param {string} userId - User identifier
 * @param {number} maxBudget - Maximum budget amount
 * @param {string} duration - Budget duration (daily, weekly, monthly, yearly)
 * @returns {Promise<Object>} Created budget data
 */
export const createBudget = async (userId, maxBudget, duration = 'monthly') => {
    try {
        const data = await litellmPost('/budget/new', {
            budget_id: userId,
            max_budget: parseFloat(maxBudget),
            budget_duration: DURATION_MAP[duration] || '30d',
            end_users: [userId],
        });
        return { success: true, data };
    } catch (error) {
        console.error('Error creating budget:', error);
        const msg = error.response?.data?.detail?.error || error.response?.data?.error || error.message;
        return { success: false, error: msg };
    }
};

/**
 * Update an existing user budget
 * Creates new budget if budgetId is not provided
 *
 * @param {string} userId - User identifier
 * @param {number} maxBudget - Maximum budget amount
 * @param {string} duration - Budget duration
 * @param {string} budgetId - Existing budget ID (null to create new)
 * @param {string} userEmail - Email of admin making the change (for audit log)
 * @returns {Promise<Object>} Updated budget data
 */
export const updateBudget = async (userId, maxBudget, duration = 'monthly', budgetId = null, userEmail = null) => {
    try {
        if (!budgetId) return await createBudget(userId, maxBudget, duration);

        const data = await litellmPost('/budget/update', {
            budget_id: budgetId,
            max_budget: parseFloat(maxBudget),
            budget_duration: DURATION_MAP[duration] || '30d',
        });

        if (data && userEmail) {
            await logAuditEntry({
                action: 'update_user_budget',
                userEmail: userEmail,
                details: {
                    budget_id: budgetId,
                    user_id: userId,
                    new_budget: parseFloat(maxBudget),
                    duration: duration
                }
            });
        }

        return { success: true, data };
    } catch (error) {
        console.error('Error updating budget:', error);
        const msg = error.response?.data?.detail?.error || error.response?.data?.error || error.message;
        return { success: false, error: msg };
    }
};

/**
 * Update team budget with custom audit logging
 * Includes team name for readable audit logs and enforcement flag
 *
 * @param {string} teamId - Team identifier
 * @param {number} maxBudget - Maximum budget amount
 * @param {string} duration - Budget duration
 * @param {string} userEmail - Email of admin making the change
 * @param {number} oldBudget - Previous budget amount (for audit trail)
 * @param {string} teamName - Human-readable team name
 * @param {boolean} enforceLimit - Whether to enforce budget limit
 * @returns {Promise<Object>} Updated team budget data
 */
export const updateTeamBudget = async (
    teamId,
    maxBudget,
    duration = 'monthly',
    userEmail = null,
    oldBudget = null,
    teamName = null,
    enforceLimit = false
) => {
    try {
        const data = await litellmPost('/team/update', {
            team_id: teamId,
            max_budget: parseFloat(maxBudget),
            budget_duration: DURATION_MAP[duration] || '30d',
        });

        if (data && userEmail) {
            await logAuditEntry({
                action: 'update_team_budget',
                userEmail: userEmail,
                details: {
                    team_id: teamId,
                    team_name: teamName || teamId,
                    old_budget: oldBudget,
                    new_budget: parseFloat(maxBudget),
                    duration: duration,
                    enforce_limit: enforceLimit
                }
            });
        }

        return { success: true, data };
    } catch (error) {
        console.error('Error updating team budget:', error);
        const msg = error.response?.data?.detail?.error || error.response?.data?.error || error.message;
        return { success: false, error: msg };
    }
};

// ========================================
// TEAM MANAGEMENT (RE-EXPORTED FROM apiHelpers)
// ========================================

/**
 * Block a team from making API requests
 * Re-exported from apiHelpers for backward compatibility
 */
export const blockTeam = blockTeamAction;

/**
 * Unblock a team to allow API requests again
 * Re-exported from apiHelpers for backward compatibility
 */
export const unblockTeam = unblockTeamAction;

// ========================================
// USER-GROUP MANAGEMENT (RE-EXPORTED FROM apiHelpers)
// ========================================

/**
 * Add a user to a group
 * Re-exported from apiHelpers for backward compatibility
 */
export const addUserToGroup = addUserToGroupAction;

/**
 * Remove a user from a group
 * Re-exported from apiHelpers for backward compatibility
 */
export const removeUserFromGroup = removeUserFromGroupAction;

// ========================================
// USER ACCOUNT MANAGEMENT (RE-EXPORTED FROM apiHelpers)
// ========================================
// Disable/Enable is implemented by removing/restoring group memberships
// since OpenWebUI doesn't have a native "disable account" feature
// ========================================

/**
 * Disable a user account by removing them from all groups
 * Stores original groups in backend for later restoration
 */
export const disableUserAccount = disableUserAccountAction;

/**
 * Enable a user account by restoring their original groups
 */
export const enableUserAccount = enableUserAccountAction;

/**
 * Check if a user is disabled (stored in backend)
 */
export const getDisabledUserStatus = getDisabledUserStatusAction;

/**
 * Get user details by ID (from apiHelpers)
 */
export const getUserById = getUserByIdAction;

// ========================================
// LITELLM TEAM MANAGEMENT
// ========================================

/**
 * Get paginated team list from LiteLLM
 *
 * @param {Object} params - Query parameters for pagination
 * @returns {Promise<Object>} Team list with pagination data
 */
export const getTeamList = async (params = {}) => {
    try {
        const data = await litellmGet('/v2/team/list', params);
        return { success: true, data };
    } catch (error) {
        console.error('Error fetching team list:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Get detailed information for a specific team
 *
 * @param {string} teamId - Team identifier
 * @returns {Promise<Object>} Team details
 */
export const getTeamInfo = async (teamId) => {
    try {
        const data = await litellmGet('/team/info', { team_id: teamId });
        return { success: true, data };
    } catch (error) {
        console.error('Error fetching team info:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Get all teams from LiteLLM
 *
 * @returns {Promise<Object>} Complete list of teams
 */
export const getLiteLLMTeams = async () => {
    try {
        const data = await litellmGet('/team/list');
        const teams = Array.isArray(data) ? data : [];
        return { success: true, data: teams };
    } catch (error) {
        console.error('Error fetching LiteLLM teams:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Fetch LiteLLM internal users
 * These are different from OpenWebUI users - they're internal to LiteLLM for key management
 *
 * @returns {Promise<Object>} { success: boolean, data: Array<{user_id, user_email, teams}> }
 */
export const getLiteLLMUsers = async () => {
    try {
        const data = await litellmGet('/user/list');
        // API returns { users: [...] }
        const users = data?.users || [];
        return { success: true, data: users };
    } catch (error) {
        console.error('Error fetching LiteLLM users:', error);
        return { success: false, error: error.message, data: [] };
    }
};

/**
 * Find group linked to a team by name matching
 * Teams and groups are linked when team.team_alias === group.name (case-insensitive)
 *
 * @param {Object} team - Team object with team_alias
 * @param {Array} groups - Array of all groups
 * @returns {Object|null} Matching group or null
 */
export const findLinkedGroup = (team, groups) => {
    if (!team?.team_alias) return null;
    return groups.find(
        g => g.name?.trim().toLowerCase() === team.team_alias.trim().toLowerCase()
    ) || null;
};

/**
 * Find team linked to a group by name matching
 * Teams and groups are linked when team.team_alias === group.name (case-insensitive)
 *
 * @param {Object} group - Group object with name
 * @param {Array} teams - Array of all teams
 * @returns {Object|null} Matching team or null
 */
export const findLinkedTeam = (group, teams) => {
    if (!group?.name) return null;
    return teams.find(
        t => t.team_alias?.trim().toLowerCase() === group.name.trim().toLowerCase()
    ) || null;
};

/**
 * Check if a name is already used by a team or group
 * Useful for validation before creating new entities
 *
 * @param {string} name - Name to check
 * @param {Array} teams - Array of teams
 * @param {Array} groups - Array of groups
 * @returns {Object} { isUsed: boolean, usedBy: 'team'|'group'|null }
 */
export const checkNameUsage = (name, teams, groups) => {
    const normalizedName = name.trim().toLowerCase();

    const teamExists = teams.some(
        t => t.team_alias?.trim().toLowerCase() === normalizedName
    );
    const groupExists = groups.some(
        g => g.name?.trim().toLowerCase() === normalizedName
    );

    if (teamExists) return { isUsed: true, usedBy: 'team' };
    if (groupExists) return { isUsed: true, usedBy: 'group' };
    return { isUsed: false, usedBy: null };
};

/**
 * Fetch team list for costing dashboard
 *
 * @returns {Promise<Object>} Team list for cost tracking
 */
export const fetchTeamList = async () => {
    try {
        const data = await litellmGet('/team/list');
        const teams = Array.isArray(data) ? data : [];
        return { success: true, data: teams };
    } catch (error) {
        return { success: false, data: [], error: error.message };
    }
};

/**
 * Create a new team in LiteLLM and corresponding group in OpenWebUI
 *
 * @param {Object} teamData - Team creation data
 * @param {string} teamData.team_alias - Team name
 * @param {number} teamData.max_budget - Maximum budget
 * @param {string} teamData.budget_duration - Budget duration (e.g., "30d")
 * @param {Array<string>} teamData.models - Array of model IDs
 * @param {string} userEmail - Email of admin creating the team (for audit log)
 * @returns {Promise<Object>} Created team data
 */
export const createTeam = async (teamData, userEmail = null) => {
    try {
        // Step 1: Create team in LiteLLM with default user
        const litellmTeamPayload = {
            team_alias: teamData.team_alias,
            max_budget: parseFloat(teamData.max_budget),
            budget_duration: teamData.budget_duration,
            models: teamData.models || [],
            blocked: false
        };

        // Add default LiteLLM user if provided
        if (teamData.defaultLiteLLMUserId) {
            litellmTeamPayload.members_with_roles = [{
                user_id: teamData.defaultLiteLLMUserId,
                role: teamData.defaultUserRole || 'admin'  // Use selected role or default to admin
            }];
        }

        const litellmData = await litellmPost('/team/new', litellmTeamPayload);

        // Step 2: Remove default_user_id if it was auto-added by LiteLLM
        // LiteLLM automatically adds 'default_user_id' when creating a team
        // We need to remove it to ensure only the selected user is added
        if (litellmData.team_id) {
            try {
                await litellmDelete('/team/member_delete', {
                    team_id: litellmData.team_id,
                    user_id: 'default_user_id'
                });
                console.log(`Removed default_user_id from team ${litellmData.team_id}`);
            } catch (removeError) {
                // Silent failure - default_user_id might not exist
                console.warn('Could not remove default_user_id (may not exist):', removeError.message);
            }
        }

        // Step 3: Create corresponding group in OpenWebUI
        const endpoints = getCurrentEndpoints();
        let newGroupId = null;
        try {
            const groupResponse = await authenticatedFetch(`${endpoints.openwebui}/api/v1/groups/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: teamData.team_alias,
                    description: `Team created for ${teamData.team_alias}`,
                    permissions: {},
                    data: {}
                })
            });

            if (!groupResponse.ok) {
                const errorText = await groupResponse.text();
                console.warn('OpenWebUI group creation failed:', errorText);
            } else {
                const groupData = await groupResponse.json();
                newGroupId = groupData?.id || null;
                console.log('OpenWebUI group created successfully for team:', teamData.team_alias, 'groupId:', newGroupId);
            }
        } catch (openwebuiError) {
            console.error('Error creating OpenWebUI group:', openwebuiError);
            // Don't fail the entire operation if OpenWebUI group creation fails
        }

        // Step 4: Add initial users to the LiteLLM team (if any selected)
        const litellmUserIds = teamData.litellmUserIds || [];
        const userRoles = teamData.userRoles || {}; // Map of userId to role
        const addedLiteLLMUsers = [];
        if (litellmData.team_id && litellmUserIds.length > 0) {
            for (const userId of litellmUserIds) {
                try {
                    await litellmPost('/team/member_add', {
                        team_id: litellmData.team_id,
                        member: {
                            user_id: userId,
                            role: userRoles[userId] || 'user'  // Use selected role or default to user
                        }
                    });
                    addedLiteLLMUsers.push(userId);
                    console.log(`Added user ${userId} to LiteLLM team ${litellmData.team_id} with role: ${userRoles[userId] || 'user'}`);
                } catch (addUserError) {
                    console.error(`Error adding user ${userId} to LiteLLM team:`, addUserError);
                }
            }
        }

        // Step 5: Add initial users to the new OpenWebUI group (if any selected)
        const initialUserIds = teamData.initialUserIds || [];
        const addedUserIds = [];
        if (newGroupId && initialUserIds.length > 0) {
            for (const userId of initialUserIds) {
                try {
                    const addResponse = await authenticatedFetch(
                        `${endpoints.openwebui}/api/v1/groups/id/${newGroupId}/users/add`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ user_ids: [userId] })
                        }
                    );
                    if (addResponse.ok) {
                        addedUserIds.push(userId);
                    } else {
                        console.warn(`Failed to add user ${userId} to OpenWebUI group ${newGroupId}`);
                    }
                } catch (addUserError) {
                    console.error(`Error adding user ${userId} to OpenWebUI group:`, addUserError);
                }
            }
        }

        // Step 5: Create API key if requested
        let apiKeyData = null;
        if (teamData.createApiKey && litellmData.team_id) {
            try {
                apiKeyData = await litellmPost('/key/generate', {
                    team_id: litellmData.team_id,
                    key_alias: `${teamData.team_alias} - Auto-generated Key`,
                    duration: null, // No expiration
                    models: teamData.models || [],
                    max_budget: parseFloat(teamData.max_budget)
                });
                console.log(`API key created for team ${teamData.team_alias}:`, apiKeyData?.key ? 'Success' : 'Failed');
            } catch (apiKeyError) {
                console.error('Error creating API key:', apiKeyError);
                // Don't fail the entire operation if API key creation fails
            }
        }

        // Step 6: Log audit entry
        if (userEmail) {
            await logAuditEntry({
                action: 'create_team',
                userEmail: userEmail,
                details: {
                    team_id: litellmData.team_id,
                    team_alias: teamData.team_alias,
                    max_budget: parseFloat(teamData.max_budget),
                    budget_duration: teamData.budget_duration,
                    models: teamData.models,
                    default_litellm_user: teamData.defaultLiteLLMUserId,
                    litellm_users_added: addedLiteLLMUsers.length,
                    openwebui_users_added: addedUserIds.length,
                    api_key_created: !!apiKeyData
                }
            });
        }

        return {
            success: true,
            data: {
                ...litellmData,
                apiKey: apiKeyData?.key || null,
                apiKeyData: apiKeyData || null
            }
        };
    } catch (error) {
        const msg = error.response?.data?.detail?.error || error.response?.data?.error || error.message;
        return { success: false, error: msg };
    }
};

/**
 * Create a new group in OpenWebUI (Ask Front End) only
 * Does NOT create corresponding LiteLLM team
 *
 * @param {Object} groupData - Group creation data
 * @param {string} groupData.name - Group name
 * @param {string} groupData.description - Group description
 * @param {Array<string>} groupData.user_ids - Initial user IDs
 * @param {string} userEmail - Admin email for audit log
 * @returns {Promise<Object>} Created group data
 */
export const createGroup = async (groupData, userEmail = null) => {
    try {
        const endpoints = getCurrentEndpoints();

        // Step 1: Create group in OpenWebUI
        const groupResponse = await authenticatedFetch(
            `${endpoints.openwebui}/api/v1/groups/create`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: groupData.name,
                    description: groupData.description || `Ask Front End group: ${groupData.name}`,
                    permissions: {},
                    data: {}
                })
            }
        );

        if (!groupResponse.ok) {
            const errorText = await groupResponse.text();
            throw new Error(`Failed to create group: ${errorText}`);
        }

        const newGroup = await groupResponse.json();
        console.log('OpenWebUI group created successfully:', groupData.name, 'groupId:', newGroup.id);

        // Step 2: Add users if provided
        const addedUserIds = [];
        if (newGroup.id && groupData.user_ids && groupData.user_ids.length > 0) {
            for (const userId of groupData.user_ids) {
                try {
                    const addResponse = await authenticatedFetch(
                        `${endpoints.openwebui}/api/v1/groups/id/${newGroup.id}/users/add`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ user_ids: [userId] })
                        }
                    );
                    if (addResponse.ok) {
                        addedUserIds.push(userId);
                    } else {
                        console.warn(`Failed to add user ${userId} to group ${newGroup.id}`);
                    }
                } catch (addUserError) {
                    console.error(`Error adding user ${userId} to group:`, addUserError);
                }
            }
        }

        // Step 3: Log audit entry
        if (userEmail) {
            await logAuditEntry({
                action: 'create_group',
                userEmail,
                details: {
                    group_id: newGroup.id,
                    name: groupData.name,
                    user_count: addedUserIds.length
                }
            });
        }

        return {
            success: true,
            data: newGroup
        };
    } catch (error) {
        console.error('Error creating group:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Create a new team in LiteLLM (Ask Engine) only
 * Does NOT create corresponding OpenWebUI group
 *
 * @param {Object} teamData - Team creation data
 * @param {string} teamData.team_alias - Team name
 * @param {number} teamData.max_budget - Maximum budget
 * @param {string} teamData.budget_duration - Budget duration (e.g., "30d")
 * @param {Array<string>} teamData.models - Array of model IDs
 * @param {string} teamData.defaultLiteLLMUserId - Default user ID
 * @param {string} teamData.defaultUserRole - Role for default user ('admin' or 'user')
 * @param {Array<string>} teamData.litellmUserIds - Additional user IDs
 * @param {Object} teamData.userRoles - Map of userId to role
 * @param {boolean} teamData.createApiKey - Whether to create API key
 * @param {Object} teamData.apiKeyConfig - API key configuration (optional)
 * @param {string} teamData.apiKeyConfig.duration - Key duration (e.g., "30d")
 * @param {string} teamData.apiKeyConfig.key_alias - Key alias
 * @param {number} teamData.apiKeyConfig.max_budget - Max budget for key
 * @param {number} teamData.apiKeyConfig.rpm_limit - Requests per minute limit
 * @param {number} teamData.apiKeyConfig.tpm_limit - Tokens per minute limit
 * @param {string} userEmail - Admin email for audit log
 * @returns {Promise<Object>} Created team data
 */
export const createTeamOnly = async (teamData, userEmail = null) => {
    try {
        // Step 1: Create team in LiteLLM
        const litellmTeamPayload = {
            team_alias: teamData.team_alias,
            max_budget: parseFloat(teamData.max_budget),
            budget_duration: teamData.budget_duration,
            models: teamData.models || [],
            blocked: false
        };

        // Add default user with selected role
        if (teamData.defaultLiteLLMUserId) {
            litellmTeamPayload.members_with_roles = [{
                user_id: teamData.defaultLiteLLMUserId,
                role: teamData.defaultUserRole || 'admin'
            }];
        }

        const litellmData = await litellmPost('/team/new', litellmTeamPayload);

        // Step 2: Remove default_user_id if it was auto-added by LiteLLM
        // LiteLLM automatically adds 'default_user_id' when creating a team
        // We need to remove it to ensure only the selected user is added
        if (litellmData.team_id) {
            try {
                await litellmDelete('/team/member_delete', {
                    team_id: litellmData.team_id,
                    user_id: 'default_user_id'
                });
                console.log(`Removed default_user_id from team ${litellmData.team_id}`);
            } catch (removeError) {
                // Silent failure - default_user_id might not exist
                console.warn('Could not remove default_user_id (may not exist):', removeError.message);
            }
        }

        // Step 3: Add additional users with their selected roles
        const litellmUserIds = teamData.litellmUserIds || [];
        const userRoles = teamData.userRoles || {};
        const addedLiteLLMUsers = [];

        if (litellmData.team_id && litellmUserIds.length > 0) {
            for (const userId of litellmUserIds) {
                try {
                    await litellmPost('/team/member_add', {
                        team_id: litellmData.team_id,
                        member: {
                            user_id: userId,
                            role: userRoles[userId] || 'user'
                        }
                    });
                    addedLiteLLMUsers.push(userId);
                } catch (addUserError) {
                    // Silent failure for individual user additions
                }
            }
        }

        // Step 4: Create API key if requested
        let apiKeyData = null;
        if (teamData.createApiKey && litellmData.team_id) {
            try {
                const apiKeyConfig = teamData.apiKeyConfig || {};
                const apiKeyPayload = {
                    team_id: litellmData.team_id,
                    models: teamData.models || []
                };

                // Add optional fields if provided
                if (apiKeyConfig.key_alias) {
                    apiKeyPayload.key_alias = apiKeyConfig.key_alias;
                } else {
                    apiKeyPayload.key_alias = `${teamData.team_alias} - Auto-generated Key`;
                }

                if (apiKeyConfig.duration) {
                    apiKeyPayload.duration = apiKeyConfig.duration;
                }

                if (apiKeyConfig.max_budget !== undefined && apiKeyConfig.max_budget !== null) {
                    apiKeyPayload.max_budget = parseFloat(apiKeyConfig.max_budget);
                }

                if (apiKeyConfig.rpm_limit !== undefined && apiKeyConfig.rpm_limit !== null) {
                    apiKeyPayload.rpm_limit = parseInt(apiKeyConfig.rpm_limit);
                }

                if (apiKeyConfig.tpm_limit !== undefined && apiKeyConfig.tpm_limit !== null) {
                    apiKeyPayload.tpm_limit = parseInt(apiKeyConfig.tpm_limit);
                }

                apiKeyData = await litellmPost('/key/generate', apiKeyPayload);
            } catch (apiKeyError) {
                // Silent failure for API key creation
            }
        }

        // Step 5: Log audit entry
        if (userEmail) {
            await logAuditEntry({
                action: 'create_team_only',
                userEmail,
                details: {
                    team_id: litellmData.team_id,
                    team_alias: teamData.team_alias,
                    max_budget: parseFloat(teamData.max_budget),
                    budget_duration: teamData.budget_duration,
                    models: teamData.models,
                    default_litellm_user: teamData.defaultLiteLLMUserId,
                    default_user_role: teamData.defaultUserRole,
                    litellm_users_added: addedLiteLLMUsers.length,
                    api_key_created: !!apiKeyData
                }
            });
        }

        return {
            success: true,
            data: {
                ...litellmData,
                apiKey: apiKeyData?.key || null,
                apiKeyData: apiKeyData || null
            }
        };
    } catch (error) {
        const msg = error.response?.data?.detail?.error || error.response?.data?.error || error.message;
        return { success: false, error: msg };
    }
};

/**
 * Delete teams from LiteLLM and corresponding groups from OpenWebUI
 *
 * IMPORTANT: This deletes the team/group, NOT the users.
 * - Users are removed from the team but remain in the system
 * - Users in multiple teams will remain in their other teams
 * - User accounts are NOT deleted
 *
 * @param {Array<string>} teamIds - Array of team IDs to delete
 * @param {Array<string>} teamAliases - Array of team aliases (for finding OpenWebUI groups)
 * @param {string} userEmail - Email of admin deleting the teams (for audit log)
 * @returns {Promise<Object>} Deletion result with user info
 */
export const deleteTeams = async (teamIds, teamAliases = [], userEmail = null) => {
    try {
        // Step 1: Get groups to capture user lists before deletion (for audit log)
        const endpoints = getCurrentEndpoints();
        const groupsResult = await getOpenWebUIGroups();
        const groups = groupsResult.data || [];

        // Collect affected users for audit log
        const affectedUsers = [];
        for (const teamAlias of teamAliases) {
            const matchingGroup = groups.find(
                g => g.name.trim().toLowerCase() === teamAlias.trim().toLowerCase()
            );
            if (matchingGroup && matchingGroup.user_ids) {
                affectedUsers.push({
                    team: teamAlias,
                    user_count: matchingGroup.user_ids.length
                });
            }
        }

        // Step 2: Delete teams from LiteLLM
        const litellmData = await litellmPost('/team/delete', {
            team_ids: teamIds
        });

        // Step 3: Delete corresponding groups from OpenWebUI
        // Note: This removes users from the group but does NOT delete user accounts
        for (const teamAlias of teamAliases) {
            try {
                const matchingGroup = groups.find(
                    g => g.name.trim().toLowerCase() === teamAlias.trim().toLowerCase()
                );

                if (matchingGroup) {
                    const deleteResponse = await authenticatedFetch(
                        `${endpoints.openwebui}/api/v1/groups/id/${matchingGroup.id}/delete`,
                        {
                            method: 'DELETE',
                            headers: { 'Content-Type': 'application/json' }
                        }
                    );

                    if (!deleteResponse.ok) {
                        const errorText = await deleteResponse.text();
                        console.warn(`Failed to delete OpenWebUI group for ${teamAlias}:`, errorText);
                    } else {
                        console.log(`OpenWebUI group deleted successfully for team: ${teamAlias}`);
                    }
                }
            } catch (openwebuiError) {
                console.error(`Error deleting OpenWebUI group for ${teamAlias}:`, openwebuiError);
                // Continue with other deletions
            }
        }

        // Step 4: Log audit entry
        if (userEmail) {
            await logAuditEntry({
                action: 'delete_teams',
                userEmail: userEmail,
                details: {
                    team_ids: teamIds,
                    team_aliases: teamAliases,
                    deleted_count: teamIds.length,
                    affected_users: affectedUsers,
                    note: 'Users were removed from teams but NOT deleted from the system'
                }
            });
        }

        return { success: true, data: litellmData, affectedUsers };
    } catch (error) {
        console.error('Error deleting teams:', error);
        const msg = error.response?.data?.detail?.error || error.response?.data?.error || error.message;
        return { success: false, error: msg };
    }
};

/**
 * Get detailed information for a specific model from LiteLLM
 *
 * @param {string} modelName - The model identifier (e.g., "PLUS.gpt-4o")
 * @returns {Promise<Object>} Model details including provider, costs, etc.
 */
export const getLiteLLMModelInfo = async (modelName) => {
    try {
        const data = await litellmGet('/model/info', { litellm_model_name: modelName });
        return { success: true, data };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// ========================================
// AUDIT LOGGING
// ========================================

/**
 * Log an audit entry to the backend
 *
 * @param {Object} entry - Audit log entry
 * @param {string} entry.action - Action being logged
 * @param {string} entry.userEmail - Email of user performing the action
 * @param {Object} entry.details - Additional details about the action
 * @returns {Promise<Object>} Log operation result
 */
export const logAuditEntry = async (entry) => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/audit-log`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action: entry.action,
                user_email: entry.userEmail,
                details: entry.details,
                timestamp: new Date().toISOString()
            }),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Audit log failed: ${response.status} - ${errorText}`);
        }
        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        console.error('[AUDIT] Failed to log entry:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Retrieve audit logs with filtering
 *
 * @param {Object} filters - Query filters
 * @param {number} filters.page - Page number
 * @param {number} filters.page_size - Items per page
 * @param {string} filters.start_date - Start date filter
 * @param {string} filters.end_date - End date filter
 * @param {string} filters.action_type - Action type filter
 * @param {string} filters.user_email - User email filter
 * @returns {Promise<Object>} Filtered audit logs
 */
export const getAuditLogs = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        if (filters.page) params.append('page', filters.page);
        if (filters.page_size) params.append('page_size', filters.page_size);
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        if (filters.action_type) params.append('action_type', filters.action_type);
        if (filters.user_email) params.append('user_email', filters.user_email);

        const queryString = params.toString();
        const url = `${config.backendApiUrl}/audit-log${queryString ? '?' + queryString : ''}`;

        const response = await authenticatedFetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
            const errorText = await response.text();
            if (response.status === 404) {
                throw new Error('Audit log endpoint not found. Please verify backend routes are loaded correctly.');
            } else if (errorText.includes('<!DOCTYPE') || errorText.includes('<!doctype')) {
                throw new Error('Backend returned HTML instead of JSON. Please ensure backend is running and routes are loaded.');
            } else {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        }

        const data = await response.json();
        // Backend returns { success: true, data: { audit_logs: [...], total: N } }
        // We want to return { success: true, data: { audit_logs: [...], total: N } }
        // So extract data.data if the backend wrapped it, otherwise use data directly
        const responseData = data.data || data;
        return { success: true, data: responseData };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// ========================================
// USER PREFERENCES API
// ========================================

/**
 * Get UI preferences for a user (e.g. hidden team IDs)
 *
 * @param {string} userEmail - User's email address
 * @returns {Promise<Object>} User preferences
 */
export const getUserPreferences = async (userEmail) => {
    try {
        const response = await authenticatedFetch(
            `${config.backendApiUrl}/user-preferences/${encodeURIComponent(userEmail)}`
        );
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        return { success: true, data: data.data || { hiddenTeamIds: [] } };
    } catch (error) {
        console.warn('[PREFS] Failed to load preferences:', error.message);
        return { success: false, data: { hiddenTeamIds: [] } };
    }
};

/**
 * Save UI preferences for a user
 *
 * @param {string} userEmail - User's email address
 * @param {Object} prefs - Preferences object { hiddenTeamIds: string[] }
 * @returns {Promise<Object>} Save result
 */
export const saveUserPreferences = async (userEmail, prefs) => {
    try {
        const response = await authenticatedFetch(
            `${config.backendApiUrl}/user-preferences/${encodeURIComponent(userEmail)}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(prefs)
            }
        );
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        return { success: true };
    } catch (error) {
        console.error('[PREFS] Failed to save preferences:', error.message);
        return { success: false, error: error.message };
    }
};

/**
 * Fetch aggregated daily activity data for the Usage & Cost dashboard.
 * Uses LiteLLM /user/daily/activity/aggregated which returns pre-aggregated
 * spend, token, and request metrics broken down by date, team (api_keys),
 * and end_user email (entities).
 *
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} Aggregated activity data
 */
export const getDepartments = async () => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/departments`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        console.error('Error fetching departments:', error);
        return { success: false, error: error.message, data: { departments: [], totalUsers: 0 } };
    }
};

export const getAllUsers = async () => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/departments/users/all`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return { success: true, users: data.users || [] };
    } catch (error) {
        console.error('Error fetching all users:', error);
        return { success: false, users: [], error: error.message };
    }
};

export const getMyTeam = async () => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/departments/my-team`);
        const data = await response.json();
        return data.success ? { isManager: data.isManager, directReports: data.directReports || [] } : { isManager: false, directReports: [] };
    } catch {
        return { isManager: false, directReports: [] };
    }
};

export const getMyDepartment = async () => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/departments/me`);
        const data = await response.json();
        return data.success ? { department: data.department, orgUnit: data.orgUnit } : { department: null, orgUnit: null };
    } catch {
        return { department: null, orgUnit: null };
    }
};

export const triggerDepartmentSync = async (graphToken) => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/departments/sync`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ graphToken })
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || `HTTP ${response.status}`);
        }
        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        console.error('Error triggering department sync:', error);
        return { success: false, error: error.message };
    }
};

export const getAdminUsers = async () => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/admin-users`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    } catch (error) {
        console.error('Error fetching admin users:', error);
        return { success: false, admins: [], protected: [] };
    }
};

export const addAdminUser = async (email) => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/admin-users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error adding admin user:', error);
        return { success: false, error: error.message };
    }
};

export const removeAdminUser = async (email) => {
    try {
        const response = await authenticatedFetch(
            `${config.backendApiUrl}/admin-users/${encodeURIComponent(email)}`,
            { method: 'DELETE' }
        );
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error removing admin user:', error);
        return { success: false, error: error.message };
    }
};

export const getUnassignedUsersCount = async () => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/provision/count`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return data.success ? (data.count ?? 0) : 0;
    } catch {
        return null;
    }
};

export const getUnassignedUsersList = async () => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/provision/unassigned`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return data.success ? (data.entries ?? []) : [];
    } catch {
        return null;
    }
};

export const getRecentlyProvisioned = async () => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/provision/recent`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return data.success ? (data.entries ?? []) : [];
    } catch {
        return null;
    }
};

export const getDisabledUsersCount = async () => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/disabled-users`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return data.success ? (data.count ?? 0) : 0;
    } catch {
        return null;
    }
};

export const getUsageCostData = async (startDate, endDate) => {
    try {
        const data = await litellmGet('/user/daily/activity/aggregated', {
            start_date: startDate,
            end_date: endDate
        });
        return { success: true, data };
    } catch (error) {
        console.error('Error fetching usage cost data:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Fetch per-user spend from LiteLLM /spend/users endpoint.
 * Returns each LiteLLM user with their spend for the current budget period.
 *
 * @param {string} [teamId] - Optional team_id to filter by team
 * @returns {Promise<Object>} List of users with spend data
 */
export const getSpendUsers = async (teamId) => {
    try {
        const params = teamId ? { team_id: teamId } : {};
        const data = await litellmGet('/spend/users', params);
        return { success: true, data };
    } catch (error) {
        console.error('Error fetching spend/users:', error);
        return { success: false, error: error.message };
    }
};

// ========================================
// LITELLM KEY MANAGEMENT
// ========================================

/**
 * List all keys for a team from LiteLLM
 *
 * @param {string} teamId - LiteLLM team_id to filter by
 * @param {number} page - Page number (default 1)
 * @param {number} size - Page size (default 25, max 100)
 * @returns {Promise<Object>} { success, data: { keys, total_count, current_page, total_pages } }
 */
export const listTeamKeys = async (teamId, page = 1, size = 25) => {
    try {
        const params = {
            page,
            size,
            return_full_object: true,
            sort_by: 'created_at',
            sort_order: 'desc'
        };

        // Only include team_id if it's provided (null means get all keys for filtering unassigned)
        if (teamId !== null && teamId !== undefined) {
            params.team_id = teamId;
        }

        const data = await litellmGet('/key/list', params);
        return { success: true, data };
    } catch (error) {
        console.error('Error listing team keys:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Get full details for a specific key
 *
 * @param {string} key - The key value (sk-...)
 * @returns {Promise<Object>} Key details
 */
export const getKeyInfo = async (key) => {
    try {
        const data = await litellmGet('/key/info', { key });
        return { success: true, data };
    } catch (error) {
        console.error('Error fetching key info:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Create a new LiteLLM virtual key for a team
 *
 * @param {Object} keyData - Key creation parameters
 * @param {string} keyData.team_id - Team to assign key to
 * @param {string} [keyData.key_alias] - Human-friendly alias
 * @param {Array<string>} [keyData.models] - Allowed models (empty = all)
 * @param {number} [keyData.max_budget] - Max budget in USD
 * @param {string} [keyData.budget_duration] - Budget reset period e.g. "30d"
 * @param {string} [keyData.duration] - Key expiry e.g. "90d" (null = never)
 * @param {number} [keyData.rpm_limit] - Requests per minute
 * @param {number} [keyData.tpm_limit] - Tokens per minute
 * @param {number} [keyData.max_parallel_requests] - Max parallel requests
 * @param {Array<string>} [keyData.tags] - Tags
 * @param {Object} [keyData.metadata] - Metadata key-value pairs
 * @param {string} userEmail - Admin email for audit log
 * @returns {Promise<Object>} Created key data including the key value
 */
export const createKey = async (keyData, userEmail = null) => {
    try {
        const body = {
            team_id: keyData.team_id,
            key_type: 'llm_api'
        };

        if (keyData.key_alias) body.key_alias = keyData.key_alias;
        if (keyData.models && keyData.models.length > 0) body.models = keyData.models;
        if (keyData.max_budget != null && keyData.max_budget !== '') body.max_budget = parseFloat(keyData.max_budget);
        if (keyData.budget_duration) body.budget_duration = keyData.budget_duration;
        if (keyData.duration) body.duration = keyData.duration;
        if (keyData.rpm_limit != null && keyData.rpm_limit !== '') body.rpm_limit = parseInt(keyData.rpm_limit, 10);
        if (keyData.tpm_limit != null && keyData.tpm_limit !== '') body.tpm_limit = parseInt(keyData.tpm_limit, 10);
        if (keyData.max_parallel_requests != null && keyData.max_parallel_requests !== '') body.max_parallel_requests = parseInt(keyData.max_parallel_requests, 10);
        if (keyData.tags && keyData.tags.length > 0) body.tags = keyData.tags;
        if (keyData.metadata && Object.keys(keyData.metadata).length > 0) body.metadata = keyData.metadata;
        if (keyData.blocked === true) body.blocked = true;

        const data = await litellmPost('/key/generate', body);

        if (userEmail) {
            await logAuditEntry({
                action: 'create_key',
                userEmail,
                details: {
                    team_id: keyData.team_id,
                    key_alias: keyData.key_alias || null,
                    models: keyData.models || []
                }
            });
        }

        return { success: true, data };
    } catch (error) {
        console.error('Error creating key:', error);
        const msg = error.response?.data?.detail?.error || error.response?.data?.error || error.message;
        return { success: false, error: msg };
    }
};

/**
 * Update an existing LiteLLM virtual key
 *
 * @param {Object} keyData - Key update parameters (must include key)
 * @param {string} userEmail - Admin email for audit log
 * @returns {Promise<Object>} Updated key data
 */
export const updateKey = async (keyData, userEmail = null) => {
    try {
        const body = { key: keyData.key };

        if (keyData.key_alias !== undefined) body.key_alias = keyData.key_alias;
        if (keyData.models !== undefined) body.models = keyData.models;
        if (keyData.max_budget != null && keyData.max_budget !== '') body.max_budget = parseFloat(keyData.max_budget);
        if (keyData.budget_duration !== undefined) body.budget_duration = keyData.budget_duration;
        if (keyData.duration !== undefined) body.duration = keyData.duration;
        if (keyData.rpm_limit != null && keyData.rpm_limit !== '') body.rpm_limit = parseInt(keyData.rpm_limit, 10);
        if (keyData.tpm_limit != null && keyData.tpm_limit !== '') body.tpm_limit = parseInt(keyData.tpm_limit, 10);
        if (keyData.max_parallel_requests != null && keyData.max_parallel_requests !== '') body.max_parallel_requests = parseInt(keyData.max_parallel_requests, 10);
        if (keyData.tags !== undefined) body.tags = keyData.tags;
        if (keyData.metadata !== undefined) body.metadata = keyData.metadata;
        if (keyData.blocked !== undefined) body.blocked = keyData.blocked;

        const data = await litellmPost('/key/update', body);

        if (userEmail) {
            await logAuditEntry({
                action: 'update_key',
                userEmail,
                details: {
                    team_id: keyData.team_id,
                    key_alias: keyData.key_alias || null
                }
            });
        }

        return { success: true, data };
    } catch (error) {
        console.error('Error updating key:', error);
        const msg = error.response?.data?.detail?.error || error.response?.data?.error || error.message;
        return { success: false, error: msg };
    }
};

/**
 * Delete one or more LiteLLM virtual keys
 *
 * @param {Array<string>} keys - Array of key values to delete
 * @param {string} teamId - Team ID (for audit log)
 * @param {string} userEmail - Admin email for audit log
 * @returns {Promise<Object>} Deletion result
 */
export const deleteKey = async (keys, teamId = null, userEmail = null) => {
    try {
        const data = await litellmPost('/key/delete', { keys });

        if (userEmail) {
            await logAuditEntry({
                action: 'delete_key',
                userEmail,
                details: {
                    team_id: teamId,
                    deleted_count: keys.length
                }
            });
        }

        return { success: true, data };
    } catch (error) {
        console.error('Error deleting key:', error);
        const msg = error.response?.data?.detail?.error || error.response?.data?.error || error.message;
        return { success: false, error: msg };
    }
};

/**
 * Block a LiteLLM virtual key
 *
 * @param {string} key - Key value to block
 * @param {string} teamId - Team ID (for audit log)
 * @param {string} userEmail - Admin email for audit log
 * @returns {Promise<Object>} Result
 */
export const blockKey = async (key, teamId = null, userEmail = null) => {
    try {
        const data = await litellmPost('/key/block', { key });

        if (userEmail) {
            await logAuditEntry({
                action: 'block_key',
                userEmail,
                details: { team_id: teamId }
            });
        }

        return { success: true, data };
    } catch (error) {
        console.error('Error blocking key:', error);
        const msg = error.response?.data?.detail?.error || error.response?.data?.error || error.message;
        return { success: false, error: msg };
    }
};

/**
 * Unblock a LiteLLM virtual key
 *
 * @param {string} key - Key value to unblock
 * @param {string} teamId - Team ID (for audit log)
 * @param {string} userEmail - Admin email for audit log
 * @returns {Promise<Object>} Result
 */
export const unblockKey = async (key, teamId = null, userEmail = null) => {
    try {
        const data = await litellmPost('/key/unblock', { key });

        if (userEmail) {
            await logAuditEntry({
                action: 'unblock_key',
                userEmail,
                details: { team_id: teamId }
            });
        }

        return { success: true, data };
    } catch (error) {
        console.error('Error unblocking key:', error);
        const msg = error.response?.data?.detail?.error || error.response?.data?.error || error.message;
        return { success: false, error: msg };
    }
};

/**
 * Fetch per-user spend for a team via the backend aggregation route.
 * The backend fetches all pages of /spend/logs/ui in parallel and returns
 * compact aggregated data (email → spend/tokens/requests).
 *
 * @param {string} teamId - LiteLLM team_id
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {Array} [knownTeamIds] - Array of user-created team IDs for health check detection
 * @returns {Promise<Object>} { success, data: [{ email, spend, tokens, requests }], total_records }
 */
export const getTeamSpend = async (teamId, startDate, endDate, knownTeamIds = []) => {
    try {
        const url = `${config.backendApiUrl}/team-spend/${encodeURIComponent(teamId)}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`;
        const response = await authenticatedFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ known_team_ids: knownTeamIds })
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Team spend fetch failed: ${response.status} - ${errorText}`);
        }
        const data = await response.json();
        return { success: true, data: data.data || [], departments: data.departments || [], orgUnits: data.orgUnits || [], total_records: data.total_records || 0 };
    } catch (error) {
        console.error('[TeamSpend] Error fetching team spend:', error);
        return { success: false, error: error.message, data: [], departments: [], orgUnits: [], total_records: 0 };
    }
};

/**
 * Fetch recent spend log entries across all teams.
 * Used by the "Recent Activity" tab in the Usage & Costs dashboard.
 *
 * @param {Object} options
 * @param {number} [options.page=1]
 * @param {number} [options.pageSize=100]
 * @param {string} [options.startDate] - YYYY-MM-DD
 * @param {string} [options.endDate]   - YYYY-MM-DD
 * @param {Array} [options.users] - Array of user objects for email hash lookup
 * @param {Array} [options.teamIds] - Array of team IDs to filter by
 * @param {Array} [options.knownTeamIds] - Array of all user-created team IDs for health check detection
 * @param {Array} [options.models] - Array of model names to filter by
 * @param {boolean} [options.includeHealthChecks=false] - Include health check logs
 * @returns {Promise<Object>} { success, data, total, page, total_pages }
 */
export const getSpendLogs = async ({ page = 1, pageSize = 100, startDate, endDate, users = [], teamIds = [], knownTeamIds = [], models = [], includeHealthChecks = false, endUser = null } = {}) => {
    try {
        const params = new URLSearchParams({ page, page_size: pageSize });
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);

        const url = `${config.backendApiUrl}/spend-logs?${params.toString()}`;

        // Use POST to send users, team_ids, known_team_ids, and models in body instead of query string to avoid 431 error
        const body = {
            users: users || [],
            team_ids: teamIds || [],
            known_team_ids: knownTeamIds || [],
            models: models || [],
            include_health_checks: includeHealthChecks,
        };
        if (endUser) body.end_user = endUser;

        const response = await authenticatedFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Spend logs fetch failed: ${response.status} - ${errorText}`);
        }
        const data = await response.json();
        return { success: true, data: data.data || [], total: data.total || 0, page: data.page || 1, total_pages: data.total_pages || 1, summary: data.summary || null };
    } catch (error) {
        console.error('[SpendLogs] Error fetching spend logs:', error);
        return { success: false, error: error.message, data: [], total: 0, total_pages: 1 };
    }
};

/**
 * Fetch daily aggregated spend data from LiteLLM
 * This endpoint returns pre-aggregated daily metrics which is much faster than fetching all individual logs
 *
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} { success, results: [{ date, metrics: { spend, successful_requests, ... } }] }
 */
export const getDailyAggregatedSpend = async (startDate, endDate) => {
    try {
        const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
        const url = `${config.backendApiUrl}/spend-logs/daily-aggregated?${params.toString()}`;

        const response = await authenticatedFetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Daily aggregated spend fetch failed: ${response.status} - ${errorText}`);
        }
        const data = await response.json();
        return { success: true, results: data.results || [] };
    } catch (error) {
        console.error('[DailyAggregatedSpend] Error fetching daily aggregated spend:', error);
        return { success: false, error: error.message, results: [] };
    }
};

/**
 * Fetch health check spend data from LiteLLM's /global/spend/tags endpoint
 * Filters for the 'litellm-internal-health-check' tag
 *
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} { success, data: { spend, requests }, error }
 */
export const getHealthCheckSpend = async (startDate, endDate) => {
    try {
        const url = `${config.backendApiUrl}/health-check-spend?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`;
        const response = await authenticatedFetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Health check spend fetch failed: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        console.error('[HealthCheckSpend] Error:', error);
        return { success: false, error: error.message, data: { spend: 0, requests: 0 } };
    }
};

/**
 * Create a new user in OpenWebUI via the admin auth endpoint.
 * The user is created with role='user' (not admin).
 *
 * @param {Object} params
 * @param {string} params.name     - Full display name
 * @param {string} params.email    - Email address
 * @param {string} params.password - Temporary password (min 8 chars)
 * @returns {Promise<Object>} { success, data } where data is the created user object
 */
export const createOpenWebUIUser = async ({ name, email, password }) => {
    try {
        const endpoints = getCurrentEndpoints();
        const url = `${endpoints.openwebui}/api/v1/auths/add`;
        const response = await authenticatedFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, email, password, role: 'user' }),
        });
        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            const msg = errData?.detail || errData?.message || `HTTP ${response.status}`;
            throw new Error(msg);
        }
        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        console.error('[CreateUser] Error creating OpenWebUI user:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Fetch detailed spend logs for heatmap visualization
 * Returns all logs with timestamps for hour-of-day analysis
 * Fetches multiple pages if needed (max 100 records per page as per LiteLLM API limit)
 *
 * @param {Object} options
 * @param {string} options.teamId - Team ID to filter by (optional)
 * @param {string} options.startDate - Start date in YYYY-MM-DD format
 * @param {string} options.endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} { success, data: [{ startTime, spend, ... }] }
 */
export const getHeatmapData = async ({ teamId, startDate, endDate }) => {
    try {
        const PAGE_SIZE = 100; // LiteLLM API maximum
        const allData = [];
        let currentPage = 1;
        let hasMore = true;

        while (hasMore) {
            const params = {
                start_date: `${startDate} 00:00:00`,
                end_date: `${endDate} 23:59:59`,
                page: currentPage,
                page_size: PAGE_SIZE
            };

            if (teamId) {
                params.team_id = teamId;
            }

            const result = await litellmGet('/spend/logs/ui', params);
            const pageData = result.data || [];

            if (pageData.length > 0) {
                allData.push(...pageData);
                currentPage++;

                if (pageData.length < PAGE_SIZE) {
                    hasMore = false;
                }
            } else {
                hasMore = false;
            }
        }

        return { success: true, data: allData };
    } catch (error) {
        console.error('[HeatmapData] Error fetching heatmap data:', error);
        return { success: false, error: error.message, data: [] };
    }
};

// ========================================
// SUPPORT STAFF MANAGEMENT
// ========================================

// Support staff management functions removed - no longer needed with simplified permissions

// ========================================
// ACKNOWLEDGED BLOCKS
// ========================================

export const getAcknowledgedBlocks = async () => {
    try {
        const res = await authenticatedFetch('/api/acknowledged-blocks');
        if (!res.ok) return { success: false, data: {} };
        return await res.json();
    } catch {
        return { success: false, data: {} };
    }
};

export const acknowledgeBlock = async (teamId, teamAlias) => {
    try {
        const res = await authenticatedFetch(`/api/acknowledged-blocks/${encodeURIComponent(teamId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ teamAlias }),
        });
        return await res.json();
    } catch {
        return { success: false };
    }
};

export const clearAcknowledgedBlock = async (teamId) => {
    try {
        const res = await authenticatedFetch(`/api/acknowledged-blocks/${encodeURIComponent(teamId)}`, {
            method: 'DELETE',
        });
        return await res.json();
    } catch {
        return { success: false };
    }
};


